import { useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  selectProductHierarchyMinimalData,
  selectProductHierarchyMinimalHasSuccessfulFetch,
} from '../store/selectors/productHierarchySelectors';
import { fetchProductHierarchyMinimal } from '../store/slices/productHierarchySlice';
import {
  buildProductLinePath,
  buildRpnPath,
  buildFpnPath,
} from '../components/manage-programs/utils/manageProgramsPathUtils';
import { RootState } from '../store/store';
import { usePermissions } from './usePermissions';
import { useSkuSearch } from './useSkuSearch';
import { canUserAccessReport } from '../utils/reportVisibilityUtils';
import * as permissionsUtils from '../utils/permissionsUtils';
import { SearchResult, SearchResultSection } from '../types/search/search.types';

// ============================================================================
// Types
// ============================================================================

export type { SearchResult, SearchResultSection } from '../types/search/search.types';

// ============================================================================
// Hook
// ============================================================================

/**
 * Reusable hook for searching across Programs, Announcements, Resources, and Reports.
 * Lazily fetches minimal hierarchy on first use.
 */
export const useProgramSearch = (
  query: string
): { sections: SearchResultSection[]; ensureDataLoaded: () => void; isSearching: boolean } => {
  const dispatch = useAppDispatch();
  const hierarchyData = useAppSelector(selectProductHierarchyMinimalData);
  const isDataLoaded = useAppSelector(selectProductHierarchyMinimalHasSuccessfulFetch);
  const announcements = useAppSelector((state: RootState) => state.announcements.announcements);
  const resources = useAppSelector((state: RootState) => state.resources.resources);
  const reportsConfig = useAppSelector((state: RootState) => state.reportsConfig.reportsConfig.data);
  const { userPermission } = usePermissions();

  const ensureDataLoaded = useCallback(() => {
    if (!isDataLoaded) {
      dispatch(fetchProductHierarchyMinimal(false));
    }
  }, [dispatch, isDataLoaded]);

  const sections = useMemo((): SearchResultSection[] => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];

    const out: SearchResultSection[] = [];

    // --- Programs ---
    const programResults: SearchResult[] = [];
    for (const pl of hierarchyData) {
      if (!pl.is_active) continue;

      if ((pl.product_line ?? '').toLowerCase().includes(q) || (pl.product_code ?? '').toLowerCase().includes(q)) {
        programResults.push({
          type: 'product-line',
          label: pl.product_line,
          displayId: pl.product_code,
          breadcrumb: [],
          path: buildProductLinePath(pl.product_code),
          favoriteNode: { hierarchy_level: 'PRODUCT_LINE', product_line_id: pl.product_line_id },
        });
      }

      for (const rpn of pl.children || []) {
        if (!rpn.is_active) continue;

        // Filter out confidential RPNs the user cannot access (uses rpn.is_confidential directly, not rpnConfidentialMap)
        if (rpn.is_confidential && !permissionsUtils.canAccessConfidentialRPN(userPermission, pl.product_line_id, true))
          continue;

        if (
          (rpn.rpn_alias ?? '').toLowerCase().includes(q) ||
          (rpn.rpn_display_id ?? '').toLowerCase().includes(q) ||
          (rpn.program_status ?? '').toLowerCase().includes(q) ||
          (rpn.is_confidential && 'confidential'.startsWith(q))
        ) {
          programResults.push({
            type: 'rpn',
            label: rpn.rpn_alias,
            displayId: rpn.rpn_display_id,
            breadcrumb: [`${pl.product_line} (${pl.product_code})`],
            path: buildRpnPath(pl.product_code, rpn.rpn_display_id),
            favoriteNode: { hierarchy_level: 'RPN', product_line_id: pl.product_line_id, rpn_id: rpn.rpn_id },
            programStatus: rpn.program_status,
            isConfidential: rpn.is_confidential,
          });
        }

        for (const fpn of rpn.children || []) {
          if (!fpn.is_active) continue;

          if ((fpn.fpn_alias ?? '').toLowerCase().includes(q) || (fpn.fpn_display_id ?? '').toLowerCase().includes(q)) {
            programResults.push({
              type: 'fpn',
              label: fpn.fpn_alias,
              displayId: fpn.fpn_display_id,
              breadcrumb: [`${pl.product_line} (${pl.product_code})`],
              path: buildFpnPath(pl.product_code, rpn.rpn_display_id, fpn.fpn_display_id),
              favoriteNode: {
                hierarchy_level: 'FPN',
                product_line_id: pl.product_line_id,
                rpn_id: rpn.rpn_id,
                fpn_id: fpn.fpn_id,
              },
              isConfidential: rpn.is_confidential,
            });
          }
        }
      }
    }
    if (programResults.length) out.push({ title: 'Programs', results: programResults });

    // --- Reports ---
    const reportResults: SearchResult[] = [];
    for (const report of reportsConfig) {
      // Filter reports by user visibility level
      if (!canUserAccessReport(report.report_visibility_level, userPermission)) continue;
      if (
        (report.report_name ?? '').toLowerCase().includes(q) ||
        (report.report_unique_code ?? '').toLowerCase().includes(q)
      ) {
        reportResults.push({
          type: 'report',
          label: report.report_name,
          displayId: report.report_group_name ?? undefined,
          breadcrumb: [],
          path: `/reports/${report.report_unique_code}`,
        });
      }
    }
    if (reportResults.length) out.push({ title: 'Reports', results: reportResults });

    // --- Announcements ---
    const announcementResults: SearchResult[] = [];
    for (const ann of announcements) {
      if ((ann.announcement_title ?? '').toLowerCase().includes(q)) {
        announcementResults.push({
          type: 'announcement',
          label: ann.announcement_title,
          breadcrumb: [],
        });
      }
    }
    if (announcementResults.length) out.push({ title: 'Announcements', results: announcementResults });

    // --- Resources ---
    const resourceResults: SearchResult[] = [];
    for (const res of resources) {
      if ((res.resource_name ?? '').toLowerCase().includes(q)) {
        resourceResults.push({
          type: 'resource',
          label: res.resource_name,
          breadcrumb: [],
          externalUrl: res.resource_link,
        });
      }
    }
    if (resourceResults.length) out.push({ title: 'Resources', results: resourceResults });

    return out;
  }, [query, hierarchyData, reportsConfig, announcements, resources, userPermission]);

  const { skuResults, isSearching } = useSkuSearch(query);

  return { sections: [...sections, ...skuResults], ensureDataLoaded, isSearching };
};
